import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Search, FolderKanban, Users2, CalendarClock, TrendingUp } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import ProjectForm from "../components/project/ProjectForm";
import { PROJECT_STATUS_COLORS, PRIORITY_COLORS } from "../components/project/projectUtils";

export default function Projects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [open, setOpen] = useState(false);

  const canCreate = ["admin", "ceo", "coo", "manager", "project_manager"].includes(user?.role);

  const load = async () => {
    try {
      const [p, c, u] = await Promise.all([
        api.get("/projects"),
        api.get("/clients").catch(() => ({ data: [] })),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setProjects(p.data);
      setClients(c.data);
      setUsers(u.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const nameOf = (id) => users.find(u => u.id === id)?.name || "—";
  const clientName = (id) => clients.find(c => c.id === id)?.client_name || "—";

  const filtered = projects.filter(p => {
    const matchesQ = !q || `${p.project_name} ${p.project_id} ${p.project_type}`.toLowerCase().includes(q.toLowerCase());
    const matchesS = statusFilter === "All" || p.status === statusFilter;
    return matchesQ && matchesS;
  });

  const statuses = ["All", "Not Started", "In Progress", "On Hold", "Completed", "Cancelled"];

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1600px]">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Delivery</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Development Projects</h1>
          <p className="text-neutral-600 text-sm mt-1">{projects.length} projects in the pipeline</p>
        </div>
        {canCreate && (
          <Button onClick={() => setOpen(true)} data-testid="create-project-btn" className="bg-orange-500 hover:bg-orange-600 text-white">
            <Plus className="w-4 h-4 mr-1" /> New Project
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search projects..." className="pl-9 bg-[#FFFFFF] border-[#E4E7EC]" data-testid="project-search-input" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {statuses.map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-${s.toLowerCase().replace(/\s+/g,'-')}`}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-mono uppercase tracking-wider transition-colors border ${statusFilter === s ? "bg-orange-100 text-orange-700 border-orange-500/30" : "bg-[#FFFFFF] text-neutral-600 border-[#E4E7EC] hover:text-neutral-900"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.length === 0 && (
          <div className="col-span-full panel p-10 text-center text-neutral-500">
            <FolderKanban className="w-8 h-8 mx-auto mb-3 opacity-50" />
            {canCreate ? "No projects yet. Click 'New Project' to start." : "You are not on any active projects yet — ask your PM to add you."}
          </div>
        )}
        {filtered.map(p => {
          const total = p.counts?.tasks || 0;
          const done = p.counts?.completed_tasks || 0;
          const pct = total ? Math.round(done / total * 100) : 0;
          return (
            <Link key={p.id} to={`/projects/${p.id}`} data-testid={`project-card-${p.id}`} className="panel panel-hover p-6 group relative overflow-hidden block">
              <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-orange-500/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="flex items-start justify-between gap-3 relative">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono uppercase text-neutral-500">{p.project_id} · {p.project_type}</div>
                  <div className="font-semibold text-lg truncate mt-1">{p.project_name}</div>
                  <div className="text-[11px] font-mono text-neutral-600 mt-1">{clientName(p.client_id)}</div>
                </div>
                <div className="flex flex-col gap-1 items-end shrink-0">
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${PROJECT_STATUS_COLORS[p.status] || "bg-neutral-100 border-neutral-300 text-neutral-700"}`}>{p.status}</span>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${PRIORITY_COLORS[p.priority] || ""}`}>{p.priority}</span>
                </div>
              </div>
              <div className="mt-4 text-[11px] font-mono text-neutral-600 line-clamp-2 min-h-[32px]">{p.description || "No description"}</div>

              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between text-[10px] font-mono text-neutral-500">
                  <span>Progress</span>
                  <span className="text-neutral-700">{done}/{total} tasks · {pct}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-[#E4E7EC] overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-[#E4E7EC] grid grid-cols-3 gap-2 text-[10px] font-mono">
                <div>
                  <div className="text-neutral-500 uppercase">Stage</div>
                  <div className="text-neutral-800 truncate">{p.current_stage || "—"}</div>
                </div>
                <div>
                  <div className="text-neutral-500 uppercase flex items-center gap-1"><Users2 className="w-3 h-3" /> PM</div>
                  <div className="text-neutral-800 truncate">{nameOf(p.project_manager_id)}</div>
                </div>
                <div>
                  <div className="text-neutral-500 uppercase flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Due</div>
                  <div className="text-neutral-800 truncate">{p.expected_completion_date || "—"}</div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <ProjectForm open={open} onOpenChange={setOpen} clients={clients} users={users} onSaved={load} />
    </div>
  );
}
